import React, { useState, useRef, useEffect, useCallback } from "react";

const i18n = require('react-redux-i18n').I18n;

const DemographicOptions = ({ options, label, name, demographicOnChange }) => {
    const normalizedOptions = (() => {
        const placeholder = Array.isArray(options[0])
            ? { id: "", name: i18n.t("select") }
            : { id: "", name: i18n.t("select") };
        const all = [placeholder, ...options];
        return all.map((opt) =>
            Array.isArray(opt) ? { id: opt[0], name: opt[1] } : { id: opt.id, name: opt.name }
        );
    })();

    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);

    const labelId  = `label-${name.toLowerCase().replace(/\s+/g, "-")}`;
    const listboxId = `listbox-${name.toLowerCase().replace(/\s+/g, "-")}`;

    const buttonRef = useRef(null);
    const listRef   = useRef(null);
    const optionRefs = useRef([]);

    const selectedOption = normalizedOptions[selectedIndex];

    // ── Close ──────────────────────────────────────────────────────────────
    const close = useCallback(() => {
        setIsOpen(false);
        buttonRef.current && buttonRef.current.focus();
    }, []);

    // ── Open — fires a custom event so sibling instances close themselves ──
    const openList = useCallback(() => {
        document.dispatchEvent(
            new CustomEvent("demographic-select-open", { detail: { id: listboxId } })
        );
        setActiveIndex(selectedIndex);
        setIsOpen(true);
    }, [listboxId, selectedIndex]);

    // ── Select option ──────────────────────────────────────────────────────
    const select = useCallback((index) => {
        setSelectedIndex(index);
        demographicOnChange(normalizedOptions[index].id);
        setIsOpen(false);
        buttonRef.current && buttonRef.current.focus();
    }, [normalizedOptions, demographicOnChange]);

    // ── Focus the listbox when it opens so keyboard events land on it ──────
    useEffect(() => {
        if (isOpen && listRef.current) {
            listRef.current.focus();
        }
    }, [isOpen]);

    // ── Scroll active option into view ─────────────────────────────────────
    useEffect(() => {
        if (isOpen && optionRefs.current[activeIndex]) {
            optionRefs.current[activeIndex].scrollIntoView({ block: "nearest" });
        }
    }, [isOpen, activeIndex]);

    // ── Close when a sibling dropdown opens ────────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (e.detail.id !== listboxId) setIsOpen(false);
        };
        document.addEventListener("demographic-select-open", handler);
        return () => document.removeEventListener("demographic-select-open", handler);
    }, [listboxId]);

    // ── Close on outside click ─────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (
                listRef.current   && !listRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [isOpen]);

    // ── Keyboard: button (closed state) ───────────────────────────────────
    const handleButtonKeyDown = (e) => {
        if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
            e.preventDefault();
            openList();
        }
    };

    // ── Keyboard: listbox (open state) ────────────────────────────────────
    const handleListKeyDown = (e) => {
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setActiveIndex((i) => Math.min(i + 1, normalizedOptions.length - 1));
                break;
            case "ArrowUp":
                e.preventDefault();
                setActiveIndex((i) => Math.max(i - 1, 0));
                break;
            case "Enter":
            case " ":
                e.preventDefault();
                select(activeIndex);
                break;
            case "Escape":
            case "Tab":
                e.preventDefault();
                close();
                break;
            default:
                break;
        }
    };

    return (
        <div className="form-group">
            <span id={labelId} className="est text-left mb-1 d-block">{label}:</span>

            {/* Hidden native select keeps .demographics class for submitDemographics() */}
            <select
                className="demographics"
                name={name}
                value={selectedOption.id}
                onChange={() => {}}
                aria-hidden="true"
                tabIndex={-1}
                style={{ position: "absolute", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
            >
                {normalizedOptions.map((opt, i) => (
                    <option key={i} value={opt.id}>{opt.name}</option>
                ))}
            </select>

            <div style={{ position: "relative" }}>
                {/* Combobox trigger button */}
                <button
                    ref={buttonRef}
                    type="button"
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-labelledby={labelId}
                    aria-controls={listboxId}
                    onClick={() => (isOpen ? close() : openList())}
                    onKeyDown={handleButtonKeyDown}
                    className="form-control text-left d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer", background: "white" }}
                >
                    <span>{selectedOption.name}</span>
                    <span aria-hidden="true" style={{ marginLeft: 8 }}>▾</span>
                </button>

                {/* Listbox — receives focus on open, handles all keyboard navigation */}
                {isOpen && (
                    <ul
                        ref={listRef}
                        id={listboxId}
                        role="listbox"
                        aria-labelledby={labelId}
                        aria-activedescendant={`${listboxId}-opt-${activeIndex}`}
                        tabIndex={0}
                        onKeyDown={handleListKeyDown}
                        style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            maxHeight: 240,
                            overflowY: "auto",
                            margin: 0,
                            padding: 0,
                            listStyle: "none",
                            background: "#fff",
                            border: "1px solid #ced4da",
                            borderRadius: 4,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            outline: "none",
                        }}
                    >
                        {normalizedOptions.map((opt, i) => {
                            const isSelected = i === selectedIndex;
                            const isActive   = i === activeIndex;
                            return (
                                <li
                                    key={i}
                                    ref={(el) => (optionRefs.current[i] = el)}
                                    role="option"
                                    aria-selected={isSelected}
                                    id={`${listboxId}-opt-${i}`}
                                    onClick={() => select(i)}
                                    onMouseEnter={() => setActiveIndex(i)}
                                    style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        background: isActive ? "#0d6efd" : "white",
                                        color: isActive ? "white" : "inherit",
                                        fontWeight: isSelected ? 600 : 400,
                                    }}
                                >
                                    {isSelected && (
                                        <span aria-hidden="true" style={{ marginRight: 6 }}>✓</span>
                                    )}
                                    {opt.name}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default DemographicOptions;
